<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
    <div class="bg-white rounded-lg p-4 md:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
      <h2 class="text-xl font-bold mb-4">{{ formType }}</h2>

      <div v-if="formFields.filter(f => !f.mappable).length > 0" class="mb-4">
        <h3 class="font-bold mb-2">{{ $t('Zusätzliche Felder') }}</h3>
        <div v-for="field in formFields.filter(f => !f.mappable)" :key="field.name" class="mb-2">
          <label class="block font-bold mb-1">{{ field.name }}</label>
          <input v-model="customFieldValues[field.name]" class="w-full border rounded px-3 py-2" :placeholder="field.name" />
        </div>
      </div>

      <div class="mb-4">
        <button 
          v-if="requiresSignature"
          @click="openPreview" 
          :disabled="!allCustomFieldsFilled"
          class="px-4 py-2 rounded font-bold"
          :class="allCustomFieldsFilled ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'"
        >
          {{ $t('Vorschau') }}
        </button>
      </div>

      <div v-if="isUnder18" class="mb-4">
        <label class="block font-bold mb-1">{{ $t('Name des Erziehungsberechtigten') }} ({{ $t('Pflichtfeld') }})</label>
        <input v-model="guardianName" class="w-full border rounded px-3 py-2" :class="validation && !guardianName ? 'bg-red-500' : ''" placeholder="Vorname Nachname" />
      </div>

      <div v-if="requiresSignature" class="mb-2">
        <label class="block font-bold mb-1">{{ $t('Unterschrift') }}</label>
        <p class="text-xs text-gray-500 mb-1">{{ $t('Bitte unterschreiben Sie im Feld unten mit dem Finger oder der Maus.') }}</p>
        <div class="border-2 border-gray-300 rounded relative bg-white touch-none">
          <canvas
            ref="canvas"
            class="w-full block touch-none cursor-crosshair"
            :style="{ height: '200px' }"
            @mousedown="startDrawing"
            @mousemove="draw"
            @mouseup="stopDrawing"
            @mouseleave="stopDrawing"
            @touchstart.prevent="startDrawingTouch"
            @touchmove.prevent="drawTouch"
            @touchend.prevent="stopDrawing"
          ></canvas>
          <button @click="clearCanvas" class="absolute top-2 right-2 px-2 py-1 bg-gray-200 rounded text-xs font-bold hover:bg-gray-300">
            {{ $t('Löschen') }}
          </button>
        </div>
      </div>

      <div class="flex gap-2 mt-4">
        <button class="full bg-gray-300 text-black" @click="$emit('close')">{{ $t('Abbrechen') }}</button>
        <button v-if="requiresSignature" class="full" @click="save">{{ $t('Speichern') }}</button>
        <button v-else 
          class="full" 
          @click="openPreview"
          :disabled="!allCustomFieldsFilled"
          :class="allCustomFieldsFilled ? '' : 'bg-gray-400 text-gray-600 cursor-not-allowed'"
        >{{ $t('Ansehen') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import API from "@/API";

export default {
  name: "SignaturePopup",
  props: {
    formType: String,
    birthdate: String,
    previewUrl: String,
    requiresSignature: { type: Boolean, default: true },
  },
  data() {
    return {
      guardianName: "",
      validation: false,
      drawing: false,
      canvasWidth: 0,
      canvasHeight: 0,
      formFields: [],
      customFieldValues: {},
    };
  },
  computed: {
    isUnder18() {
      if (!this.birthdate) return false;
      try {
        const birth = new Date(this.birthdate);
        const today = new Date();
        let age = today.getFullYear() - birth.getFullYear();
        const m = today.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
          age--;
        }
        return age < 18;
      } catch (e) {
        return false;
      }
    },
    allCustomFieldsFilled() {
      const unmappableFields = this.formFields.filter(f => !f.mappable);
      if (unmappableFields.length === 0) return true;
      return unmappableFields.every(f => this.customFieldValues[f.name] && this.customFieldValues[f.name].trim() !== '');
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.resizeCanvas();
      window.addEventListener("resize", this.resizeCanvas);
      if (this.formType) {
        this.loadFormFields();
      }
    });
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.resizeCanvas);
  },
  methods: {
    loadFormFields() {
      API.getEhrenamtFormFields(this.formType, (r, status) => {
        if (status === 200 && r.fields) {
          this.formFields = r.fields;
        }
      });
    },
    openPreview() {
      if (this.previewUrl) {
        const customFieldsJson = encodeURIComponent(JSON.stringify(this.customFieldValues));
        const previewUrlWithFields = `${this.previewUrl}&custom_fields=${customFieldsJson}`;
        window.open(previewUrlWithFields, '_blank');
      }
    },
    resizeCanvas() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = 200;
      this.canvasWidth = rect.width;
      this.canvasHeight = 200;
    },
    getCtx() {
      const canvas = this.$refs.canvas;
      if (!canvas) return null;
      const ctx = canvas.getContext("2d");
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#000";
      return ctx;
    },
    getPos(e) {
      const canvas = this.$refs.canvas;
      const rect = canvas.getBoundingClientRect();
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    },
    getTouchPos(e) {
      const canvas = this.$refs.canvas;
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0] || e.changedTouches[0];
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      };
    },
    startDrawing(e) {
      this.drawing = true;
      const ctx = this.getCtx();
      const pos = this.getPos(e);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    },
    draw(e) {
      if (!this.drawing) return;
      const ctx = this.getCtx();
      const pos = this.getPos(e);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    },
    stopDrawing() {
      this.drawing = false;
    },
    startDrawingTouch(e) {
      this.drawing = true;
      const ctx = this.getCtx();
      const pos = this.getTouchPos(e);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    },
    drawTouch(e) {
      if (!this.drawing) return;
      const ctx = this.getCtx();
      const pos = this.getTouchPos(e);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    },
    clearCanvas() {
      const canvas = this.$refs.canvas;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    },
    save() {
      this.validation = true;
      if (this.isUnder18 && !this.guardianName.trim()) {
        return;
      }
      
      // Skip signature validation if signature is not required
      if (this.requiresSignature) {
        const canvas = this.$refs.canvas;
        const ctx = canvas.getContext("2d");
        const pixelBuffer = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
        let hasContent = false;
        for (let i = 3; i < pixelBuffer.length; i += 4) {
          if (pixelBuffer[i] !== 0) {
            hasContent = true;
            break;
          }
        }
        if (!hasContent) {
          alert("Bitte unterschreiben Sie das Formular.");
          return;
        }
      }
      
      const signatureData = this.requiresSignature ? this.$refs.canvas.toDataURL("image/png") : "";
      API.submitEhrenamtForm({
        formType: this.formType,
        signatureData: signatureData,
        guardianName: this.isUnder18 ? this.guardianName : "",
        customFields: this.customFieldValues,
      }, (r, status) => {
        if (status === 200) {
          this.$emit("saved");
        } else {
          alert("Fehler beim Speichern des Formulars.");
        }
      });
    },
  },
};
</script>

<style scoped>
.touch-none {
  touch-action: none;
}
</style>
